import { FC } from 'react';

import { isEmpty } from 'lodash/fp';

import { observer } from 'mobx-react';

import { useStores } from 'mobx/hooks/useStores';

import { formatDate } from 'utils/DateUtils';

import { getMissedDosesReasonsText } from 'utils/OralOncoUtils';

import { ReportQuestionAnswer } from 'models/OralReport/OralReport.shared';
import {
  ActiveMedicationAnswer,
  MissedDosesAnswer,
  OralQuestionTypeV3,
  OralReportV3,
  RemainingDosesAnswer
} from 'models/OralReport/OralReportV3';

import { OralReportSummaryProps } from 'components/Ticket/TicketRow/OralReport/OralReport.shared';

import { OralReportCycleV3 } from 'components/Ticket/TicketRow/OralReport/OralReportCycleV3';
import { OralReportRow } from 'components/Ticket/TicketRow/OralReport/OralReportRow';

export const OralReportSummaryV3: FC<OralReportSummaryProps<OralReportV3>> = observer(
  ({ reportId, oral, patient, isCallLoggerVersion }) => {
    const { constantsStore } = useStores();

    const activeMedication = oral[OralQuestionTypeV3.ActiveMedication];
    const startOfCycle = oral[OralQuestionTypeV3.StartOfCycle];
    const regimenAdherence = oral[OralQuestionTypeV3.RegimenAdherence];
    const missedDoses = oral[OralQuestionTypeV3.MissedDoses];
    const dosesRemaining = oral[OralQuestionTypeV3.DosesRemaining];
    const refillRequest = oral[OralQuestionTypeV3.RefillRequest];
    const { previousReportDate } = oral;

    const missedDosesReasonsText = getMissedDosesReasonsText(
      missedDoses!,
      constantsStore.missedDosesReasonsMap
    );

    const renderActiveMedicationValue = (value: boolean | ActiveMedicationAnswer) => {
      switch (value) {
        case false:
          return 'No';
        case true:
          return 'Yes';
        case ActiveMedicationAnswer.NotReceived:
          return 'Not Received';
      }
    };

    const renderGeneralAnswerValue = (value: boolean) => {
      return value === true ? 'Yes' : 'No';
    };

    const renderMissedDosesValue = (value: MissedDosesAnswer) => {
      switch (value) {
        case MissedDosesAnswer.None:
          return 'None';
        case MissedDosesAnswer.OneOrTwo:
          return '1-2';
        case MissedDosesAnswer.ThreeOrFour:
          return '3-4';
        case MissedDosesAnswer.MoreThanFour:
          return '4+';
        default:
          return value;
      }
    };

    const renderRemainingDosesValue = (
      dosesRemainingAnswer: ReportQuestionAnswer<RemainingDosesAnswer, OralQuestionTypeV3>
    ) => {
      if (dosesRemainingAnswer.isAnswered) {
        const { value } = dosesRemainingAnswer;
        switch (value) {
          case RemainingDosesAnswer.NoDoses:
            return 'None';
          case RemainingDosesAnswer.LowDoses:
            return '<1 Week';
          case RemainingDosesAnswer.WeeksDoses:
            return '1-2 Weeks';
          case RemainingDosesAnswer.ManyDoses:
            return '2+ Weeks';
        }
      }

      if (dosesRemainingAnswer.isIDontKnow) {
        return 'Unknown';
      }

      return null;
    };

    const isDosesRemainingAnsweredOrIDontKnow =
      dosesRemaining?.isAnswered || dosesRemaining?.isIDontKnow;

    return (
      <>
        {activeMedication?.isAnswered && (
          <OralReportRow isCallLoggerVersion={isCallLoggerVersion} title="Active Medication">
            {renderActiveMedicationValue(activeMedication.value!)}
            {activeMedication.customMessage && ` (${activeMedication.customMessage})`}
          </OralReportRow>
        )}
        {startOfCycle?.isAnsweredOrDontKnow && (
          <OralReportRow isCallLoggerVersion={isCallLoggerVersion} title="Start of Cycle">
            <OralReportCycleV3 reportId={reportId} oral={oral} patient={patient} />
          </OralReportRow>
        )}
        {missedDoses?.isAnsweredOrDontKnow && (
          <OralReportRow isCallLoggerVersion={isCallLoggerVersion} title="Missed Doses">
            {missedDoses.isIDontKnow ? (
              'Unknown'
            ) : (
              <>
                {renderMissedDosesValue(missedDoses.value!)}
                {` since ${
                  previousReportDate
                    ? formatDate(previousReportDate, 'MMMM Do, YYYY')
                    : 'Start of Cycle'
                }`}
                {!isEmpty(missedDosesReasonsText) ? ` - ${missedDosesReasonsText}` : ''}
              </>
            )}
          </OralReportRow>
        )}
        {regimenAdherence?.isAnswered && (
          <OralReportRow isCallLoggerVersion={isCallLoggerVersion} title="Regimen Adherence">
            {renderGeneralAnswerValue(regimenAdherence.value!)}
            {regimenAdherence.customMessage ? ` (${regimenAdherence.customMessage})` : ''}
          </OralReportRow>
        )}
        {isDosesRemainingAnsweredOrIDontKnow && (
          <OralReportRow isCallLoggerVersion={isCallLoggerVersion} title="Doses Remaining">
            {renderRemainingDosesValue(dosesRemaining)}
          </OralReportRow>
        )}
        {refillRequest?.isAnswered && (
          <OralReportRow isCallLoggerVersion={isCallLoggerVersion} title="Refill Request">
            {refillRequest.value ? 'Yes' : 'No'}
            {refillRequest.customMessage ? ` (${refillRequest.customMessage})` : ''}
          </OralReportRow>
        )}
      </>
    );
  }
);
